<template>
	<div class="relative h-full">
		<!-- Durante o scroll renderizamos só a capa (img estática) para reduzir o
		     custo de paint e evitar os "buracos" no fling rápido do iOS. O Embla
		     completo só é montado quando o scroll para (idle). -->
		<div v-if="isCoverOnly" class="size-full">
			<slot :image="limitedImages[0]" :index="0" />
		</div>

		<Carousel
			v-else
			class="size-full"
			:opts="{
				loop: true
			}"
			:plugins="[WheelGesturesPlugin()]"
			@init-api="setupCarouselApi"
		>
			<CarouselContent class="h-full">
				<CarouselItem
					v-for="(image, index) in limitedImages"
					:key="'watermarkUrl' in image ? image.watermarkUrl : image.url"
					@click="handleItemClick()"
				>
					<!-- Lazy slides (forward-only): renderiza a imagem pesada só da capa
					     + o próximo, revelando o seguinte conforme o usuário avança.
					     Os demais ficam como placeholder do mesmo tamanho (Embla mantém
					     todos os slides para snap/loop e o contador de dots). -->
					<slot v-if="isSlideRevealed(index)" :image="image" :index="index" />
					<div v-else class="size-full" aria-hidden="true" />
				</CarouselItem>
			</CarouselContent>

			<template v-if="limitedImages?.length > 1">
				<CarouselPrevious
					:is-mouse-over-carousel="isMouseOverCardHeader"
					:class="{
						'hidden md:block': variant === 'fullFace'
					}"
					@click="handleImageScrollClick()"
				>
					<IconMaterialSymbolsLightChevronLeft class="size-8 stroke-2" />
				</CarouselPrevious>

				<CarouselNext
					:is-mouse-over-carousel="isMouseOverCardHeader"
					:class="{
						'hidden md:block': variant === 'fullFace'
					}"
					@click="handleImageScrollClick()"
				>
					<IconMaterialSymbolsLightChevronRight class="size-8 stroke-2" />
				</CarouselNext>
			</template>
		</Carousel>

		<!-- Marcador de slides sempre visível (inclusive na capa, antes do Embla),
		     já que o total de imagens é conhecido — evita o "pop" dos dots ao
		     promover. Clicar num dot durante a capa promove o Embla e navega. -->
		<div
			v-if="limitedImages?.length > 1"
			:class="
				cn(
					dotsVariants?.({
						size: variant === 'fullFace' ? 'lg' : 'sm'
					}),
					'opacity-100 group-hover/card:opacity-100 md:opacity-0'
				)
			"
		>
			<button
				v-for="(_, index) in limitedImages"
				:key="`card-carousel-index:${index}`"
				class="size-1 min-w-1 cursor-pointer"
				:class="isCurrentItem(index) ? 'bg-white' : 'bg-white/50'"
				@click="$event => goTo($event, index)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import type {
	CardCarouselProps,
	ImageClickedEvent,
	ImageScrolledEvent
} from '.';
import type { CarouselApi } from '../../ui/carousel';

import { cn } from '@/utils';
import { Carousel } from '#components';
import { isNumber } from '~~/utils';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { CarouselNext, CarouselPrevious } from '../../ui/carousel';
import { useCarouselScrolling } from './useCarouselScrolling';

const props = defineProps<CardCarouselProps>();
const emit = defineEmits<{
	'image-clicked': [ImageClickedEvent];
	'image-scrolled': [ImageScrolledEvent];
}>();

const carouselApi = ref<CarouselApi | null>(null);

const currentItem = ref<number>(0);
const clickCounts = ref(0);

const limitedImages = computed(() => props.images.slice(0, 5));

// Estratégia "lazy upgrade, sem downgrade":
// o card renderiza apenas a capa (img estática, barata de pintar) e só é
// promovido para o Embla completo quando o scroller está parado. Uma vez
// promovido, NUNCA volta a ser capa — assim cards já prontos não são
// manipulados no DOM durante novos flings; apenas os que entram na tela durante
// o scroll ficam baratos (capa) até o scroll parar. Isso ataca a causa do
// "buraco" (tiles novos que não pintam a tempo) sem o churn de remontar o que
// já existe.
const { isScrolling, lazyInitialMount } = useCarouselScrolling();

// Em repouso, monta o Embla na hora (comportamento padrão) — a menos que o
// provedor peça `lazyInitialMount` (capa-primeiro, promovido no idle).
const hasMountedEmbla = ref(!lazyInitialMount && !isScrolling.value);

function promoteToEmbla(): void {
	if (!isScrolling.value) hasMountedEmbla.value = true;
}

watch(isScrolling, scrolling => {
	if (!scrolling) hasMountedEmbla.value = true;
});

// Handle do agendamento de promoção (capa-primeiro), cancelado no unmount para
// não rodar o callback (e segurar o closure do componente) depois de destruído.
let promoteHandle: number | undefined;

onMounted(() => {
	if (hasMountedEmbla.value || isScrolling.value) return;
	// Capa-primeiro: promove para o Embla quando o navegador estiver ocioso,
	// sem bloquear o primeiro paint. Se o usuário começar a rolar antes disso,
	// o `watch` acima promove assim que o scroll parar.
	if (typeof requestIdleCallback !== 'undefined') {
		promoteHandle = requestIdleCallback(promoteToEmbla, { timeout: 500 });
	} else {
		promoteHandle = window.setTimeout(promoteToEmbla, 200);
	}
});

onUnmounted(() => {
	if (promoteHandle === undefined) return;
	if (typeof requestIdleCallback !== 'undefined') {
		cancelIdleCallback(promoteHandle);
	} else {
		clearTimeout(promoteHandle);
	}
});

const isCoverOnly = computed(
	() => !hasMountedEmbla.value && limitedImages.value.length > 0
);

// Lazy slides (forward-only): o conjunto de slides "revelados" (com a imagem
// real montada) começa com a capa + o próximo e só CRESCE conforme o usuário
// avança — pré-revelando o próximo para a transição de avanço ficar suave.
// Como só cresce, ao voltar no swipe os anteriores já estão revelados (sem
// churn). O contador de dots é sempre baseado no total (limitedImages),
// independente do que foi revelado, então a contagem nunca muda.
const revealedIndices = ref(new Set<number>());

function revealSlideAndNext(index: number): void {
	const total = limitedImages.value.length;
	if (total === 0) return;
	revealedIndices.value.add(index);
	revealedIndices.value.add((index + 1) % total);
}

revealSlideAndNext(0);

function isSlideRevealed(index: number): boolean {
	return revealedIndices.value.has(index);
}

function setupCarouselApi(api: CarouselApi) {
	carouselApi.value = api;
	carouselApi.value?.on('select', () => {
		currentItem.value = carouselApi.value?.selectedScrollSnap() || 0;
		revealSlideAndNext(currentItem.value);
	});
}

function isCurrentItem(index: number): boolean {
	return currentItem.value === index;
}

function goTo(event: MouseEvent, index: number) {
	event.stopPropagation();
	event.preventDefault();

	// Clique num dot durante a fase capa-only: promove para o Embla e navega
	// assim que ele montar (jump instantâneo, sem animar a partir do índice 0).
	if (isCoverOnly.value) {
		hasMountedEmbla.value = true;
		currentItem.value = index;
		revealSlideAndNext(index);
		nextTick(() => carouselApi.value?.scrollTo(index, true));
		return;
	}

	carouselApi.value?.scrollTo(index);
}

function handleItemClick() {
	const image = props.images[currentItem.value];
	const photoUrl =
		isNumber(currentItem.value) && image
			? ('watermarkUrl' in image && image.watermarkUrl) ||
				('url' in image ? image.url : undefined)
			: undefined;

	const photoPosition = isNumber(currentItem.value)
		? currentItem.value + 1
		: undefined;

	emit('image-clicked', {
		currentItem: currentItem.value,
		photoUrl,
		photoPosition
	});
}

function handleImageScrollClick() {
	clickCounts.value += 1;

	emit('image-scrolled', {
		clickCount: clickCounts.value
	});
}
</script>
